import { createFileRoute } from '@tanstack/react-router';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import { type CoachMessage, type CoachMode, type CoachSession, coachApi } from '../lib/api';

export const Route = createFileRoute('/coach')({
  component: CoachPage,
});

type UiMode = 'free' | 'knowledge' | 'training' | 'postmortem';

/** v1.10: 训练模式下的子模式 — 业务对话 vs 反骗训练 */
type TrainingMode = 'business' | 'fraud';

interface TrainingScenario {
  id: string;
  name: string;
  description: string;
  difficulty: 1 | 2 | 3;
  customerPersona: string;
  customerMessage: string;
  category: string;
  /** v1.10: 反骗分类 (fraud 模式才用) */
  fraudCategory?: string;
  /** v1.10: ANTI-FRAUD.md 引用 */
  referenceDoc?: string;
}

interface TrainingChatMsg {
  id?: string;
  role: 'customer' | 'user';
  content: string;
  score?: number;
  redlineFlags?: string[];
  feedback?: string;
  matchedBehaviors?: string[];
  missingBehaviors?: string[];
  /** v1.10: 反骗模式才有 */
  grade?: 'A' | 'B' | 'C';
  gradeLabel?: string;
}

const MODE_MAP: Record<
  UiMode,
  { coachMode: CoachMode; label: string; icon: string; hint: string }
> = {
  free: {
    coachMode: 'free',
    label: '问答',
    icon: 'message-square',
    hint: '随便问 — FOB/CIF 合规/外汇/找客',
  },
  knowledge: {
    coachMode: 'knowledge',
    label: '知识',
    icon: 'book-open',
    hint: '基于 10 条三角架构知识',
  },
  training: {
    coachMode: 'quiz',
    label: '训练',
    icon: 'target',
    hint: '8 场景对话式模拟 · AI 客户扮角 · 实时评分',
  },
  postmortem: {
    coachMode: 'scenario',
    label: '复盘',
    icon: 'bar-chart-3',
    hint: '订单 close 后自动复盘',
  },
};

function CoachPage() {
  const [uiMode, setUiMode] = useState<UiMode>('free');
  return (
    <div>
      <div className="flex items-center justify-between mb-4 sm:mb-5">
        <h1 className="text-xl sm:text-2xl font-semibold">AI 外贸教练</h1>
      </div>

      <div className="flex gap-1 p-1 bg-surface-2 rounded mb-3 text-sm w-fit">
        {(Object.keys(MODE_MAP) as UiMode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setUiMode(m)}
            className={`px-3 py-1.5 rounded text-xs inline-flex items-center gap-1.5 ${
              uiMode === m ? 'bg-surface shadow-sm font-medium' : 'text-ink-2'
            }`}
          >
            <Icon name={MODE_MAP[m].icon} size="xs" strokeWidth={2} />
            {MODE_MAP[m].label}
          </button>
        ))}
      </div>

      <p className="text-xs text-ink-3 mb-3">{MODE_MAP[uiMode].hint}</p>

      {uiMode === 'training' ? (
        <TrainingPanel />
      ) : (
        <ChatPanel uiMode={uiMode} setUiMode={setUiMode} />
      )}
    </div>
  );
}

// ============ Chat Panel (free / knowledge / postmortem) ============
function ChatPanel({
  uiMode,
  setUiMode,
}: {
  uiMode: UiMode;
  setUiMode: (m: UiMode) => void;
}) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>(
    [],
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // v1.7: 流式 streaming — assistant 消息一边收一边拼, streamingId 指向正在生长的那条
  const [streamingId, setStreamingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sources, setSources] = useState<string[]>([]);
  const [history, setHistory] = useState<CoachSession[]>([]);
  // v1.7: 持有当前流的 AbortController, 用于"停止生成"按钮
  const abortRef = useRef<AbortController | null>(null);

  /**
   * v1.7: 发送消息
   * 1) 优先用 SSE 流式 (/coach/chat/stream) — 实时看字字出
   * 2) 流式失败 (网络/版本) → fallback 到非流式 /coach/chat
   */
  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setError(null);
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    setSending(true);

    const { coachMode } = MODE_MAP[uiMode];
    // 临时 id: 流 start 事件到达后会被替换; 用来定位"正在生长的 assistant 消息"
    const tempStreamingId = `streaming-${Date.now()}`;
    setMessages((m) => [...m, { role: 'assistant', content: '' }]);
    setStreamingId(tempStreamingId);

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      await coachApi.chatStream({
        message: text,
        sessionId: sessionId ?? undefined,
        mode: coachMode,
        signal: ac.signal,
        onStart: (info) => {
          setSessionId(info.sessionId);
          setStreamingId(info.messageId);
          if (info.sources) setSources(info.sources);
        },
        onDelta: (delta) => {
          setMessages((m) => {
            // 找到 streamingId 指向的 assistant, 拼接 delta
            const idx = m.findIndex((x) => x.role === 'assistant');
            if (idx === -1) return m;
            const arr = m.slice();
            const target = arr[idx];
            if (!target) return m;
            arr[idx] = { role: 'assistant', content: target.content + delta };
            return arr;
          });
        },
        onDone: (info) => {
          setMessages((m) => {
            const idx = m.findIndex((x) => x.role === 'assistant');
            if (idx === -1) return m;
            const arr = m.slice();
            const target = arr[idx];
            if (!target) return m;
            arr[idx] = { role: 'assistant', content: info.content || target.content };
            return arr;
          });
          if (info.sources) setSources(info.sources);
          setStreamingId(null);
          coachApi
            .listSessions()
            .then((h) => setHistory(h.sessions))
            .catch(() => {});
        },
        onError: (err) => {
          setError(err.fallback ?? err.message ?? 'AI 教练出错');
          // v1.7: 流报错保留已流出的部分 (不删), 标记流结束
          setStreamingId(null);
        },
      });
    } catch (e) {
      // 流式入口失败 (网络/HTTP 错): fallback 到非流
      if (ac.signal.aborted) {
        // 用户主动停止: 静默处理
        setStreamingId(null);
        return;
      }
      // 真错了: fallback
      try {
        const res = await coachApi.chat({
          message: text,
          sessionId: sessionId ?? undefined,
          mode: coachMode,
        });
        setSessionId(res.sessionId);
        // 替换临时 assistant 消息
        setMessages((m) => {
          const arr = m.slice();
          const idx = arr.findIndex((x) => x.role === 'assistant');
          if (idx >= 0) arr[idx] = { role: 'assistant', content: res.reply };
          return arr;
        });
        setSources(res.sources ?? []);
        coachApi
          .listSessions()
          .then((h) => setHistory(h.sessions))
          .catch(() => {});
      } catch (fallbackErr) {
        const err = fallbackErr as { message?: string; body?: string };
        const msg = err.body ? JSON.parse(err.body) : null;
        setError(msg?.fallback ?? msg?.message ?? err.message ?? 'AI 教练出错');
        // 移除空 assistant 占位
        setMessages((m) => {
          const arr = m.slice();
          const idx = arr.findIndex((x) => x.role === 'assistant' && x.content === '');
          if (idx >= 0) arr.splice(idx, 1);
          return arr;
        });
      }
    } finally {
      setSending(false);
      setStreamingId(null);
      abortRef.current = null;
    }
  }

  /** v1.7: 停止生成 (AbortController) */
  function stop() {
    abortRef.current?.abort();
  }

  function newChat() {
    abortRef.current?.abort();
    setSessionId(null);
    setMessages([]);
    setSources([]);
    setError(null);
    setStreamingId(null);
  }

  async function loadSession(s: CoachSession) {
    try {
      const { session, messages: msgs } = await coachApi.getSession(s.id);
      setSessionId(session.id);
      setMessages(
        msgs.map((m: CoachMessage) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        })),
      );
      setError(null);
      setStreamingId(null);
    } catch (e) {
      const err = e as { message?: string };
      setError(err.message ?? '加载会话失败');
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
      <div>
        <div className="bg-surface border border-line rounded flex flex-col h-[60vh] sm:h-[520px]">
          <div className="flex-1 p-4 overflow-y-auto flex flex-col gap-3">
            {messages.length === 0 && (
              <div className="text-center text-ink-3 text-sm py-12">
                {uiMode === 'postmortem' ? (
                  <>
                    <Icon name="bar-chart-3" size="xl" className="text-ink-3 mx-auto mb-2" />
                    <p>复盘由订单关闭时自动生成</p>
                    <p className="text-xs mt-1">在订单详情页关闭订单后, 这里会显示复盘</p>
                  </>
                ) : (
                  <>
                    <p>问个外贸问题开始</p>
                    <p className="text-xs mt-1">比如: FOB 风险何时转移?</p>
                  </>
                )}
              </div>
            )}
            {messages.map((m, i) => {
              // v1.7: 流式时, 最后一条 assistant 消息末尾加闪烁光标
              const isStreaming =
                m.role === 'assistant' && streamingId !== null && i === messages.length - 1;
              return (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: chat message list, no stable id available client-side
                  key={i}
                  className={`flex max-w-[80%] ${m.role === 'user' ? 'self-end' : 'self-start'}`}
                >
                  <div
                    className={`px-3 py-2 rounded text-sm whitespace-pre-wrap ${
                      m.role === 'user' ? 'bg-accent text-white' : 'bg-surface-2 border border-line'
                    }`}
                  >
                    {m.content}
                    {isStreaming && (
                      <span
                        aria-hidden="true"
                        className="inline-block w-[2px] h-[1em] align-text-bottom ml-0.5 bg-current animate-caret"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {error && (
            <div className="mx-3 mb-2 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}

          {sources.length > 0 && (
            <div className="mx-3 mb-2 px-2 py-1.5 text-xs text-ink-3 bg-surface-2 border border-line rounded flex items-center gap-1.5">
              <Icon name="paperclip" size="xs" strokeWidth={2} />
              引用知识: {sources.join(', ')}
            </div>
          )}

          <div className="flex gap-2 p-3 border-t border-line">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && !sending && send()}
              placeholder={
                uiMode === 'postmortem' ? '复盘由订单关闭时自动生成' : '问个外贸问题… (⏎ 发送)'
              }
              disabled={uiMode === 'postmortem'}
              className="flex-1 px-3 py-2 border border-line rounded text-sm disabled:bg-surface-2 disabled:text-ink-3"
              aria-label="向教练提问"
            />
            {sending ? (
              <button
                type="button"
                onClick={stop}
                className="px-4 py-2 bg-bad text-white rounded text-sm font-medium hover:opacity-90"
              >
                ⏹ 停止生成
              </button>
            ) : (
              <button
                type="button"
                onClick={send}
                disabled={!input.trim() || uiMode === 'postmortem'}
                className="px-4 py-2 bg-accent text-white rounded text-sm font-medium disabled:opacity-50"
              >
                发送
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 text-right">
          <button
            type="button"
            onClick={newChat}
            className="px-3 py-1.5 text-xs border border-line rounded hover:bg-surface-2"
          >
            + 新对话
          </button>
        </div>
      </div>

      {/* 历史侧栏 */}
      <div className="bg-surface border border-line rounded p-3 h-fit order-first lg:order-none">
        <h3 className="text-xs font-semibold mb-2 text-ink-2">最近对话</h3>
        {history.length === 0 ? (
          <p className="text-xs text-ink-3">还没历史</p>
        ) : (
          <ul className="space-y-1">
            {history.slice(0, 10).map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => loadSession(s)}
                  className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-surface-2 truncate"
                >
                  <span className="text-ink-3 mr-1 inline-flex items-center">
                    {s.mode === 'free' && <Icon name="message-square" size="xs" strokeWidth={2} />}
                    {s.mode === 'knowledge' && <Icon name="book-open" size="xs" strokeWidth={2} />}
                    {s.mode === 'quiz' && <Icon name="target" size="xs" strokeWidth={2} />}
                    {s.mode === 'scenario' && <Icon name="bar-chart-3" size="xs" strokeWidth={2} />}
                  </span>
                  {s.title || '(无标题)'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============ Training Panel (v1.8 P1: 对话式模拟 + v1.10: 反骗训练) ============
// 流程: 选模式 (业务 / 反骗) → 选场景 → 跟 AI 扮的客户/骗子对话 → 多轮评分 → 总分复盘
type TrainingPhase = 'picker' | 'chat' | 'done';

function TrainingPanel() {
  const [phase, setPhase] = useState<TrainingPhase>('picker');
  // v1.10: 业务训练 vs 反骗训练 子模式
  const [mode, setMode] = useState<TrainingMode>('business');
  const [scenarios, setScenarios] = useState<TrainingScenario[]>([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);
  const [selectedScenario, setSelectedScenario] = useState<TrainingScenario | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<TrainingChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turn, setTurn] = useState(0);
  const [maxTurns, setMaxTurns] = useState(5);
  const [totalScore, setTotalScore] = useState(0);
  const [lastScore, setLastScore] = useState<number | null>(null);
  const [lastFeedback, setLastFeedback] = useState<string>('');
  const [lastRedlineFlags, setLastRedlineFlags] = useState<string[]>([]);
  const [lastMissing, setLastMissing] = useState<string[]>([]);
  const [lastGrade, setLastGrade] = useState<'A' | 'B' | 'C' | null>(null);
  const [lastGradeLabel, setLastGradeLabel] = useState<string>('');
  const navigate = useNavigate();

  // 根据 mode 加载场景
  useEffect(() => {
    let cancelled = false;
    setLoadingScenarios(true);
    setError(null);
    const fetcher =
      mode === 'fraud' ? coachApi.fraudListScenarios() : coachApi.listTrainingScenarios();
    fetcher
      .then((res) => {
        if (cancelled) return;
        // 业务训练返 category (string), 反骗返 fraudCategory (string)
        // 统一成 TrainingScenario shape
        const list: TrainingScenario[] = res.scenarios.map((s) => {
          const x = s as TrainingScenario & { fraudCategory?: string; referenceDoc?: string };
          return {
            id: x.id,
            name: x.name,
            description: x.description,
            difficulty: x.difficulty,
            customerPersona: x.customerPersona,
            customerMessage: x.customerMessage,
            category: x.category ?? (mode === 'fraud' ? 'fraud' : ''),
            fraudCategory: x.fraudCategory,
            referenceDoc: x.referenceDoc,
          };
        });
        setScenarios(list);
      })
      .catch((e) => {
        const err = e as { message?: string; body?: string };
        const msg = err.body ? JSON.parse(err.body) : null;
        if (!cancelled) setError(msg?.message ?? err.message ?? '加载场景失败');
      })
      .finally(() => {
        if (!cancelled) setLoadingScenarios(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  async function startScenario(s: TrainingScenario) {
    setError(null);
    setSelectedScenario(s);
    setMessages([]);
    setInput('');
    setTurn(0);
    setLastScore(null);
    setLastFeedback('');
    setLastRedlineFlags([]);
    setLastMissing([]);
    setLastGrade(null);
    setLastGradeLabel('');
    setTotalScore(0);
    try {
      const res =
        mode === 'fraud'
          ? await coachApi.fraudStart({ scenarioId: s.id })
          : await coachApi.trainingStart({ scenarioId: s.id });
      setSessionId(res.sessionId);
      setMaxTurns(res.maxTurns);
      setMessages([{ role: 'customer', content: res.customerMessage }]);
      setPhase('chat');
    } catch (e) {
      const err = e as { message?: string; body?: string };
      const msg = err.body ? JSON.parse(err.body) : null;
      setError(msg?.message ?? err.message ?? '开始训练失败');
    }
  }

  async function send() {
    if (!sessionId) return;
    const text = input.trim();
    if (!text || sending) return;
    setError(null);
    setSending(true);
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    try {
      const res =
        mode === 'fraud'
          ? await coachApi.fraudRespond({ sessionId, userMessage: text })
          : await coachApi.trainingRespond({ sessionId, userMessage: text });
      setLastScore(res.score);
      setLastFeedback(res.feedback);
      setLastRedlineFlags(res.redlineFlags ?? []);
      setLastMissing(res.missingBehaviors ?? []);
      setTurn(res.turn);
      // 反骗模式有 grade 字段 (fraudRespond 才有)
      if (mode === 'fraud') {
        const fraudRes = res as { grade?: 'A' | 'B' | 'C'; gradeLabel?: string };
        setLastGrade(fraudRes.grade ?? null);
        setLastGradeLabel(fraudRes.gradeLabel ?? '');
      } else {
        setLastGrade(null);
        setLastGradeLabel('');
      }
      if (res.customerMessage && !res.done) {
        setMessages((m) => [...m, { role: 'customer', content: res.customerMessage }]);
      } else if (res.done) {
        setMessages((m) => [...m, { role: 'customer', content: res.customerMessage }]);
        setTotalScore(res.totalScore ?? 0);
        setPhase('done');
      }
    } catch (e) {
      const err = e as { message?: string; body?: string };
      const msg = err.body ? JSON.parse(err.body) : null;
      setError(msg?.message ?? err.message ?? '提交失败');
    } finally {
      setSending(false);
    }
  }

  function restart() {
    setPhase('picker');
    setSelectedScenario(null);
    setSessionId(null);
    setMessages([]);
    setInput('');
    setTurn(0);
    setLastScore(null);
    setLastFeedback('');
    setLastRedlineFlags([]);
    setLastMissing([]);
    setLastGrade(null);
    setLastGradeLabel('');
    setTotalScore(0);
    setError(null);
  }

  // ============ Picker 阶段 ============
  if (phase === 'picker') {
    return (
      <div>
        {/* v1.10: 模式切换 (业务训练 / 反骗训练) */}
        <div className="flex gap-1 p-1 bg-surface-2 rounded mb-3 text-sm w-fit">
          <button
            type="button"
            onClick={() => setMode('business')}
            className={`px-3 py-1.5 rounded text-xs inline-flex items-center gap-1.5 ${
              mode === 'business' ? 'bg-surface shadow-sm font-medium' : 'text-ink-2'
            }`}
          >
            <Icon name="briefcase" size="xs" strokeWidth={2} />
            业务训练
          </button>
          <button
            type="button"
            onClick={() => setMode('fraud')}
            className={`px-3 py-1.5 rounded text-xs inline-flex items-center gap-1.5 ${
              mode === 'fraud' ? 'bg-surface shadow-sm font-medium text-bad' : 'text-ink-2'
            }`}
          >
            <Icon name="siren" size="xs" strokeWidth={2} />
            反骗训练
            <span className="text-[10px] text-bad font-bold">⚠</span>
          </button>
        </div>

        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold inline-flex items-center gap-1.5">
            {mode === 'fraud' && <Icon name="alert-triangle" size="sm" className="text-bad" />}
            {mode === 'fraud' ? '反骗训练场景' : '选择训练场景'}
          </h2>
          <span className="text-xs text-ink-3">{scenarios.length} 个场景 · 1-3 星难度</span>
        </div>
        {mode === 'fraud' && (
          <p className="text-xs text-bad bg-red-50 border border-red-200 rounded px-2 py-1.5 mb-3 inline-flex items-center gap-1.5">
            <Icon name="alert-triangle" size="xs" strokeWidth={2} />
            训练: 客户是骗子, 你需要识别 + 拒骗。任何"换账户 / 垫付 / 货发非洲"都是 0 分。
          </p>
        )}
        {loadingScenarios ? (
          <div className="text-sm text-ink-3 py-8 text-center">加载中…</div>
        ) : scenarios.length === 0 ? (
          <div className="text-sm text-ink-3 py-8 text-center">
            暂无可用场景 {error ? `(${error})` : ''}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {scenarios.map((s) => {
              const isFraud = mode === 'fraud';
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => startScenario(s)}
                  className={`text-left p-4 bg-surface rounded hover:shadow-sm transition border ${
                    isFraud
                      ? 'border-red-300 hover:border-red-500'
                      : 'border-line hover:border-accent'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-sm font-semibold text-ink-1 inline-flex items-center gap-1">
                      {isFraud && <Icon name="alert-triangle" size="xs" className="text-bad" />}
                      {s.name}
                    </span>
                    <span
                      className={`text-xs inline-flex items-center gap-0.5 ${
                        isFraud ? 'text-bad' : 'text-warn'
                      }`}
                      title={`难度 ${s.difficulty}/3`}
                    >
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Icon
                          key={`star-${i}-${i < s.difficulty ? 'on' : 'off'}`}
                          name="star"
                          size="xs"
                          strokeWidth={2}
                          className={i < s.difficulty ? '' : 'text-ink-3'}
                        />
                      ))}
                    </span>
                  </div>
                  <p className="text-xs text-ink-3 mb-2 line-clamp-2">{s.description}</p>
                  <p className="text-xs text-ink-2 line-clamp-3 italic">{s.customerPersona}</p>
                  <div
                    className={`mt-2 text-xs inline-flex items-center gap-1 ${
                      isFraud ? 'text-bad' : 'text-accent'
                    }`}
                  >
                    开始
                    <Icon name="arrow-right" size="xs" strokeWidth={2} />
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {error && <div className="mt-3 text-xs text-bad">{error}</div>}
      </div>
    );
  }

  // ============ Done 阶段 ============
  if (phase === 'done' && selectedScenario) {
    return (
      <TrainingDoneView
        scenario={selectedScenario}
        totalScore={totalScore}
        maxTurns={maxTurns}
        messages={messages}
        mode={mode}
        onRestart={restart}
        onGoToOrder={() => {
          // 跳到 /orders 页面 (有预填表单入口)
          navigate({ to: '/orders' });
        }}
      />
    );
  }

  // ============ Chat 阶段 ============
  if (!selectedScenario) return null;
  const isFraud = mode === 'fraud';
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold inline-flex items-center gap-1.5">
            {isFraud && <Icon name="alert-triangle" size="sm" className="text-bad" />}
            {selectedScenario.name}
          </h2>
          <p className="text-xs text-ink-3">
            第 {turn} / {maxTurns} 轮 · {isFraud ? '骗子' : '客户'}:{' '}
            {selectedScenario.customerPersona.slice(0, 40)}…
          </p>
        </div>
        <button
          type="button"
          onClick={restart}
          className="text-xs px-2 py-1 border border-line rounded hover:bg-surface-2"
        >
          退出
        </button>
      </div>

      {/* 进度条 */}
      <div className="h-1.5 bg-surface-2 rounded mb-3 overflow-hidden">
        <div
          className={`h-full transition-all ${isFraud ? 'bg-bad' : 'bg-accent'}`}
          style={{ width: `${(turn / maxTurns) * 100}%` }}
        />
      </div>

      {/* 聊天区 */}
      <div
        className={`bg-surface border rounded flex flex-col h-[480px] ${
          isFraud ? 'border-red-300' : 'border-line'
        }`}
      >
        <div className="flex-1 p-4 overflow-y-auto flex flex-col gap-3">
          {messages.map((m, i) => {
            const isCustomer = m.role === 'customer';
            return (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: chat message list, no stable id available client-side
                key={i}
                className={`flex max-w-[80%] ${isCustomer ? 'self-start' : 'self-end'}`}
              >
                <div
                  className={`px-3 py-2 rounded text-sm whitespace-pre-wrap ${
                    isCustomer
                      ? isFraud
                        ? 'bg-red-50 border border-red-200 text-ink-1'
                        : 'bg-surface-2 border border-line text-ink-1'
                      : 'bg-accent text-white'
                  }`}
                >
                  {isCustomer && (
                    <div
                      className={`text-[10px] mb-1 font-medium inline-flex items-center gap-1 ${
                        isFraud ? 'text-bad' : 'text-ink-3'
                      }`}
                    >
                      <Icon
                        name={isFraud ? 'alert-triangle' : 'message-square'}
                        size="xs"
                        strokeWidth={2}
                      />
                      {isFraud ? '骗子' : '客户'}
                    </div>
                  )}
                  {m.content}
                </div>
              </div>
            );
          })}
        </div>

        {/* 评分卡: 只在 user 回复后展示 */}
        {lastScore !== null && (
          <div
            className={`mx-3 mb-2 p-2 border rounded text-xs ${
              isFraud
                ? lastRedlineFlags.length > 0
                  ? 'border-red-400 bg-red-50'
                  : 'border-line bg-surface-2'
                : 'border-line bg-surface-2'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <ScoreBar score={lastScore} />
                <span className="font-semibold text-ink-1">{lastScore}/100</span>
                {isFraud && lastGrade && (
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      lastGrade === 'A'
                        ? 'bg-good text-white'
                        : lastGrade === 'B'
                          ? 'bg-warn text-white'
                          : 'bg-bad text-white'
                    }`}
                  >
                    {lastGradeLabel || lastGrade}
                  </span>
                )}
              </div>
              {lastRedlineFlags.length > 0 && (
                <span className="text-bad font-medium inline-flex items-center gap-1">
                  <Icon name="alert-triangle" size="xs" strokeWidth={2} />
                  踩红线 ({lastRedlineFlags.length})
                </span>
              )}
            </div>
            <p className="text-ink-2">{lastFeedback}</p>
            {lastMissing.length > 0 && (
              <p className="text-ink-3 mt-1">未覆盖: {lastMissing.slice(0, 3).join('、')}</p>
            )}
          </div>
        )}

        {error && (
          <div className="mx-3 mb-2 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded">
            {error}
          </div>
        )}

        <div className="flex gap-2 p-3 border-t border-line">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && !sending && send()}
            placeholder={
              isFraud
                ? '回复骗子 (识别 + 拒骗 + 替代方案)…'
                : '输入你的回复 (报价 / 谈判 / 提问 / 让步…)'
            }
            disabled={sending}
            className="flex-1 px-3 py-2 border border-line rounded text-sm disabled:bg-surface-2"
            aria-label={isFraud ? '回复骗子' : '回复客户'}
          />
          <button
            type="button"
            onClick={send}
            disabled={!input.trim() || sending}
            className={`px-4 py-2 text-white rounded text-sm font-medium disabled:opacity-50 ${
              isFraud ? 'bg-bad' : 'bg-accent'
            }`}
          >
            {sending ? '发送中…' : '发送'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ 评分进度条 (绿/黄/红) ============
function ScoreBar({ score }: { score: number }) {
  const color = score >= 80 ? 'bg-good' : score >= 50 ? 'bg-warn' : 'bg-bad';
  return (
    <div className="w-20 h-1.5 bg-line rounded overflow-hidden">
      <div
        className={`h-full ${color} transition-all`}
        style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
      />
    </div>
  );
}

// ============ Done 阶段: 总分 + 复盘 + 跳订单 ============
function TrainingDoneView({
  scenario,
  totalScore,
  maxTurns,
  messages,
  mode,
  onRestart,
  onGoToOrder,
}: {
  scenario: TrainingScenario;
  totalScore: number;
  maxTurns: number;
  messages: TrainingChatMsg[];
  mode: TrainingMode;
  onRestart: () => void;
  onGoToOrder: () => void;
}) {
  const navigate = useNavigate();
  // 5 段反馈: 报价合理 / 语气 / 红线 / 跟单 / 改进 (业务模式)
  // 反骗模式: 识别骗术 / 拒骗决心 / 红线 / 替代方案 / 改进
  const userMsgs = messages.filter((m) => m.role === 'user');
  const userScores = userMsgs
    .map((m) => (typeof m.score === 'number' ? m.score : null))
    .filter((n): n is number => n !== null);
  const avg =
    userScores.length > 0
      ? Math.round(userScores.reduce((a, b) => a + b, 0) / userScores.length)
      : 0;
  const redlineCount = userMsgs.reduce((acc, m) => acc + (m.redlineFlags?.length ?? 0), 0);
  const matchedTotal = userMsgs.reduce((acc, m) => acc + (m.matchedBehaviors?.length ?? 0), 0);
  const missingTotal = userMsgs.reduce((acc, m) => acc + (m.missingBehaviors?.length ?? 0), 0);

  const isFraud = mode === 'fraud';
  const feedbackSections = isFraud
    ? [
        {
          key: '识别',
          label: '骗术识别',
          score:
            matchedTotal > 0
              ? Math.min(100, Math.round((matchedTotal / (matchedTotal + missingTotal || 1)) * 100))
              : 0,
          note:
            redlineCount > 0
              ? `触发了 ${redlineCount} 条红线 — 说明没识破关键骗术信号`
              : matchedTotal >= missingTotal
                ? `覆盖 ${matchedTotal} 个识别点, 漏 ${missingTotal} 个`
                : `漏掉 ${missingTotal} 个识别点, 多看 docs/ANTI-FRAUD.md Part 0`,
        },
        {
          key: '拒骗',
          label: '拒骗决心',
          score: redlineCount === 0 ? 100 : Math.max(0, 100 - redlineCount * 30),
          note:
            redlineCount === 0
              ? '未踩任何红线, 完美拒骗'
              : `触发了 ${redlineCount} 条红线 (转账/换账户/垫付/发非洲/发第三方) — 100% 骗子特征`,
        },
        {
          key: '方案',
          label: '替代方案',
          score: avg,
          note:
            avg >= 70
              ? '提供了清晰替代方案 (信保/T/T 30/70/原账户)'
              : '替代方案较弱, 建议明确指出"原账户 + T/T 30/70 + 信保"',
        },
        {
          key: '语气',
          label: '语气专业度',
          score: avg,
          note: avg >= 70 ? '语气专业坚定, 让骗子知难而退' : '语气可以更坚定, 不要犹豫',
        },
        {
          key: '改进',
          label: '改进方向',
          score: Math.min(100, avg + 10),
          note: `参考 ${scenario.referenceDoc ?? 'docs/ANTI-FRAUD.md'}, 熟记"换账户/垫付/发非洲"三大铁律`,
        },
      ]
    : [
        {
          key: '报价',
          label: '报价合理性',
          score: Math.max(0, avg - (redlineCount > 0 ? 20 : 0)),
          note:
            avg >= 70
              ? '报价区间合理, 有竞争力'
              : avg >= 40
                ? '报价有空间, 客户可能砍价'
                : '报价偏离市场, 建议重谈',
        },
        {
          key: '语气',
          label: '语气 & 礼节',
          score: avg,
          note: avg >= 70 ? '语气专业, 客户感受良好' : '语气需更专业, 注意敬语',
        },
        {
          key: '红线',
          label: '红线合规',
          score: redlineCount === 0 ? 100 : Math.max(0, 100 - redlineCount * 30),
          note:
            redlineCount === 0
              ? '未踩任何红线'
              : `触发了 ${redlineCount} 条红线, 复盘对话看下哪些表述客户反感`,
        },
        {
          key: '跟单',
          label: '跟单节奏',
          score: avg,
          note: avg >= 70 ? '跟单节奏好, 推进有力' : '跟单较慢, 客户可能流失',
        },
        {
          key: '改进',
          label: '改进方向',
          score: Math.min(100, avg + 10),
          note: '多关注客户关切, 主动确认细节 (交期/认证/付款条件)',
        },
      ];

  // 业务模式用 trophy 渐进; 反骗模式按红线数 + 平均分决定 icon
  const summaryIcon = isFraud
    ? redlineCount > 0
      ? 'alert-triangle'
      : avg >= 80
        ? 'check-circle'
        : avg >= 60
          ? 'check-circle'
          : 'alert-triangle'
    : totalScore >= 80
      ? 'trophy'
      : totalScore >= 60
        ? 'thumbs-up'
        : totalScore >= 40
          ? 'dumbbell'
          : 'book-open';

  return (
    <div className="bg-surface border border-line rounded p-6">
      <div className="text-center mb-5">
        <div
          className={`mb-2 inline-flex items-center justify-center w-16 h-16 rounded-full ${
            isFraud ? 'bg-red-50 text-bad' : 'bg-accent-soft text-accent'
          }`}
        >
          <Icon name={summaryIcon} size="xl" strokeWidth={1.75} />
        </div>
        <h2 className="text-2xl font-semibold mb-1 inline-flex items-center gap-1.5">
          {isFraud && <Icon name="alert-triangle" size="md" className="text-bad" />}
          {isFraud ? '反骗训练完成' : '训练完成'} · {scenario.name}
        </h2>
        <p className="text-sm text-ink-2">
          {isFraud ? '平均分' : '总分'}{' '}
          <span className={`font-bold ${isFraud ? 'text-bad' : 'text-accent'}`}>{totalScore}</span>{' '}
          / 100 · 共 {maxTurns} 轮
          {isFraud && (
            <span className="ml-2 text-xs text-ink-3">
              (识别 {matchedTotal} 漏 {missingTotal} · 红线 {redlineCount})
            </span>
          )}
        </p>
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
          <Icon name="bar-chart-3" size="sm" className={isFraud ? 'text-bad' : 'text-accent'} />5
          维度复盘
        </h3>
        <ul className="space-y-2">
          {feedbackSections.map((s) => (
            <li key={s.key} className="p-3 border border-line rounded bg-surface-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-ink-1">{s.label}</span>
                <ScoreBar score={s.score} />
              </div>
              <p className="text-xs text-ink-2">{s.note}</p>
            </li>
          ))}
        </ul>
      </div>

      <div className="text-center space-y-2">
        {isFraud ? (
          <>
            <button
              type="button"
              onClick={onRestart}
              className="px-6 py-2.5 bg-bad text-white rounded text-sm font-medium hover:opacity-90"
            >
              <Icon name="rotate-ccw" size="sm" strokeWidth={2} />
              换个骗术再来
            </button>
            <div>
              <a
                href="/coach"
                onClick={(e) => {
                  e.preventDefault();
                  navigate({ to: '/coach' });
                }}
                className="text-xs text-ink-3 hover:underline"
              >
                ← 回到训练 tab 选业务模式
              </a>
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onGoToOrder}
              className="px-6 py-2.5 bg-accent text-white rounded text-sm font-medium hover:opacity-90"
            >
              <Icon name="rocket" size="sm" strokeWidth={2} />
              做真实订单试试 (跳到 /orders)
            </button>
            <div>
              <button
                type="button"
                onClick={onRestart}
                className="px-4 py-1.5 text-xs border border-line rounded hover:bg-surface-2"
              >
                换个场景再来
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
